import { useMemo } from 'react';
import { Control, useController } from 'react-hook-form';

import { BrandDetailsFormValues, ValidationRule } from '../../types';
import {
  createEinValidator,
  createMaxLengthValidator,
  createPatternValidator,
  createRequiredValidator,
  isValidInput,
} from '../../validators';
import regexExpressions from '@/constants/regexExpressions';
import { EntityTypes } from '@/shared_elements/enums';

const useControllers = (
  control: Control<BrandDetailsFormValues, any, BrandDetailsFormValues>,
  validationRules: ValidationRule[]
) => {
  const einValidationRule = useMemo(
    () => validationRules.find((rule) => rule.attributeName === 'ein'),
    [validationRules]
  );

  const {
    field: { value: entityType, onChange: setEntityType },
    fieldState: { error: entityTypeError },
  } = useController({
    control,
    name: 'entityType',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select Entity Type'),
      },
    },
  });
  const {
    field: {
      disabled: companyNameDisabled,
      value: companyName,
      onChange: setCompanyName,
      onBlur: validateCompanyName,
    },
    fieldState: { error: companyNameError },
  } = useController({
    control,
    name: 'companyName',
    disabled: entityType === EntityTypes.SoleProprietor,
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter Brand Legal Name'),
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });
  const {
    field: {
      value: displayName,
      onChange: setDisplayName,
      onBlur: validateDisplayName,
    },
    fieldState: { error: displayNameError },
  } = useController({
    control,
    name: 'displayName',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter Brand DBA Name'),
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });
  const {
    field: { value: country, onChange: setCountry },
    fieldState: { error: countryError },
  } = useController({
    control,
    name: 'country',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select Country'),
      },
    },
  });
  const {
    field: {
      disabled: einIssuingCountryDisabled,
      value: einIssuingCountry,
      onChange: setEinIssuingCountry,
    },
    fieldState: { error: einIssuingCountryError },
  } = useController({
    control,
    name: 'einIssuingCountry',
    disabled:
      !country || !entityType || entityType === EntityTypes.SoleProprietor,
  });
  const {
    field: {
      disabled: einDisabled,
      value: ein,
      onChange: setEin,
      onBlur: validateEin,
    },
    fieldState: { error: einError },
  } = useController({
    control,
    name: 'ein',
    disabled:
      !country || !entityType || entityType === EntityTypes.SoleProprietor,
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          `Please enter ${einIssuingCountry === 'US' ? 'EIN' : 'Tax Number/ID'}`
        ),
        ...(einIssuingCountry === 'US'
          ? { isValidEin: createEinValidator(einValidationRule) }
          : { withValidMaxLength: createMaxLengthValidator(21) }),
      },
    },
  });
  const {
    field: {
      disabled: altBusinessIdTypeDisabled,
      value: altBusinessIdType,
      onChange: setAltBusinessIdType,
    },
    fieldState: { error: altBusinessIdTypeError },
  } = useController({
    control,
    name: 'altBusinessIdType',
    disabled:
      !country || !entityType || entityType === EntityTypes.SoleProprietor,
  });
  const {
    field: {
      disabled: altBusinessIdDisabled,
      value: altBusinessId,
      onChange: setAltBusinessId,
      onBlur: validateAltBusinessId,
    },
    fieldState: { error: altBusinessIdError },
  } = useController({
    control,
    name: 'altBusinessId',
    disabled:
      !country ||
      !entityType ||
      !altBusinessIdType ||
      entityType === EntityTypes.SoleProprietor,
    rules: {
      validate: {
        withValidMaxLength: createMaxLengthValidator(50),
      },
    },
  });
  const {
    field: {
      disabled: streetDisabled,
      value: street,
      onChange: setStreet,
      onBlur: validateStreet,
    },
    fieldState: { error: streetError },
  } = useController({
    control,
    name: 'street',
    disabled: !country || !entityType,
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter Address/Street'),
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });
  const {
    field: {
      disabled: cityDisabled,
      value: city,
      onChange: setCity,
      onBlur: validateCity,
    },
    fieldState: { error: cityError },
  } = useController({
    control,
    name: 'city',
    disabled: !country || !entityType,
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter City'),
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });
  const {
    field: {
      disabled: stateDisabled,
      value: state,
      onChange: setState,
      onBlur: validateState,
    },
    fieldState: { error: stateError },
  } = useController({
    control,
    name: 'state',
    disabled: !country || !entityType,
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          `Please ${country === 'US' ? 'select State' : 'enter State/Region'}`
        ),
        ...(country === 'US'
          ? {}
          : {
              isValidInput,
              withValidMaxLength: createMaxLengthValidator(50),
            }),
      },
    },
  });
  const {
    field: {
      disabled: postalCodeDisabled,
      value: postalCode,
      onChange: setPostalCode,
      onBlur: validatePostalCode,
    },
    fieldState: { error: postalCodeError },
  } = useController({
    control,
    name: 'postalCode',
    disabled: !country || !entityType,
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          `Please enter ${country === 'US' ? 'ZIP Code' : 'Postal Code'}`
        ),
        withValidMaxLength: createMaxLengthValidator(20),
        ...(country === 'US'
          ? {
              withValidPattern: createPatternValidator(
                regexExpressions.zip,
                'Invalid Zip Code'
              ),
            }
          : { isValidInput }),
      },
    },
  });
  const {
    field: {
      disabled: websiteDisabled,
      value: website,
      onChange: setWebsite,
      onBlur: validateWebsite,
    },
    fieldState: { error: websiteError },
  } = useController({
    control,
    name: 'website',
    disabled: !country || !entityType,
    rules: {
      validate: {
        ...(entityType === EntityTypes.PubliclyTradedCompany
          ? {
              isRequired: createRequiredValidator('Please enter Website'),
            }
          : {}),
        withValidMaxLength: createMaxLengthValidator(255),
        withValidPattern: createPatternValidator(
          regexExpressions.url,
          'Invalid Website'
        ),
      },
    },
  });
  const {
    field: {
      disabled: stockSymbolDisabled,
      value: stockSymbol,
      onChange: setStockSymbol,
      onBlur: validateStockSymbol,
    },
    fieldState: { error: stockSymbolError },
  } = useController({
    control,
    name: 'stockSymbol',
    disabled:
      !country ||
      !entityType ||
      entityType !== EntityTypes.PubliclyTradedCompany,
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Stock Symbol'),
        withValidMaxLength: createMaxLengthValidator(10),
      },
    },
  });
  const {
    field: {
      disabled: stockExchangeDisabled,
      value: stockExchange,
      onChange: setStockExchange,
    },
    fieldState: { error: stockExchangeError },
  } = useController({
    control,
    name: 'stockExchange',
    disabled:
      !country ||
      !entityType ||
      entityType !== EntityTypes.PubliclyTradedCompany,
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select Stock Exchange'),
      },
    },
  });
  const {
    field: {
      disabled: verticalTypeDisabled,
      value: verticalType,
      onChange: setVerticalType,
    },
    fieldState: { error: verticalTypeError },
  } = useController({
    control,
    name: 'vertical',
    disabled: !country || !entityType,
    rules: {
      validate: {
        ...(entityType !== EntityTypes.SoleProprietor
          ? {
              isRequired: createRequiredValidator(
                'Please select Vertical Type'
              ),
            }
          : {}),
      },
    },
  });
  const {
    field: {
      disabled: referenceIdDisabled,
      value: referenceId,
      onChange: setReferenceId,
      onBlur: validateReferenceId,
    },
    fieldState: { error: referenceIdError },
  } = useController({
    control,
    name: 'referenceId',
    disabled: !country || !entityType,
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(50),
        ...(entityType === EntityTypes.SoleProprietor
          ? { isRequired: createRequiredValidator('Please enter Reference ID') }
          : {}),
      },
    },
  });
  const {
    field: {
      disabled: firstNameDisabled,
      value: firstName,
      onChange: setFirstName,
      onBlur: validateFirstName,
    },
    fieldState: { error: firstNameError },
  } = useController({
    control,
    name: 'firstName',
    disabled:
      !country || !entityType || entityType !== EntityTypes.SoleProprietor,
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter First Name'),
        withValidMaxLength: createMaxLengthValidator(100),
        withValidPattern: createPatternValidator(
          regexExpressions.name,
          'Invalid First Name'
        ),
      },
    },
  });
  const {
    field: {
      disabled: lastNameDisabled,
      value: lastName,
      onChange: setLastName,
      onBlur: validateLastName,
    },
    fieldState: { error: lastNameError },
  } = useController({
    control,
    name: 'lastName',
    disabled:
      !country || !entityType || entityType !== EntityTypes.SoleProprietor,
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Last Name'),
        withValidMaxLength: createMaxLengthValidator(100),
        withValidPattern: createPatternValidator(
          regexExpressions.name,
          'Invalid Last Name'
        ),
      },
    },
  });
  const {
    field: {
      disabled: mobilePhoneDisabled,
      value: mobilePhone,
      onChange: setMobilePhone,
      onBlur: validateMobilePhone,
    },
    fieldState: { error: mobilePhoneError },
  } = useController({
    control,
    name: 'mobilePhone',
    disabled:
      !country || !entityType || entityType !== EntityTypes.SoleProprietor,
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter Mobile Phone'),
        withValidMaxLength: createMaxLengthValidator(20),
      },
    },
  });

  return {
    companyName,
    setCompanyName,
    validateCompanyName,
    companyNameDisabled,
    companyNameError,
    displayName,
    setDisplayName,
    validateDisplayName,
    displayNameError,
    entityType,
    setEntityType,
    entityTypeError,
    country,
    setCountry,
    countryError,
    ein,
    setEin,
    validateEin,
    einDisabled,
    einError,
    einIssuingCountry,
    setEinIssuingCountry,
    einIssuingCountryDisabled,
    einIssuingCountryError,
    altBusinessIdType,
    setAltBusinessIdType,
    altBusinessIdTypeDisabled,
    altBusinessIdTypeError,
    altBusinessId,
    setAltBusinessId,
    validateAltBusinessId,
    altBusinessIdDisabled,
    altBusinessIdError,
    street,
    setStreet,
    validateStreet,
    streetDisabled,
    streetError,
    city,
    setCity,
    validateCity,
    cityDisabled,
    cityError,
    state,
    setState,
    validateState,
    stateDisabled,
    stateError,
    postalCode,
    setPostalCode,
    validatePostalCode,
    postalCodeDisabled,
    postalCodeError,
    website,
    setWebsite,
    validateWebsite,
    websiteDisabled,
    websiteError,
    stockSymbol,
    setStockSymbol,
    validateStockSymbol,
    stockSymbolDisabled,
    stockSymbolError,
    stockExchange,
    setStockExchange,
    stockExchangeDisabled,
    stockExchangeError,
    verticalType,
    setVerticalType,
    verticalTypeDisabled,
    verticalTypeError,
    referenceId,
    setReferenceId,
    validateReferenceId,
    referenceIdDisabled,
    referenceIdError,
    firstName,
    setFirstName,
    validateFirstName,
    firstNameDisabled,
    firstNameError,
    lastName,
    setLastName,
    validateLastName,
    lastNameDisabled,
    lastNameError,
    mobilePhone,
    setMobilePhone,
    validateMobilePhone,
    mobilePhoneDisabled,
    mobilePhoneError,
  };
};

export default useControllers;
